import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Stack,
  Typography,
  Button,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// MUI Icons
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockIcon from "@mui/icons-material/Lock";
import HomeIcon from "@mui/icons-material/Home";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import VerifiedIcon from "@mui/icons-material/Verified";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import moment from "moment";

// Components
import ProfileBanner from "../../components/ProfileBanner";
import ArtistContent from "../../components/ArtistContent";
import TipPopup from "../../components/TipPopup";
import SuccessPopup from "../../components/SuccessPopup";
import AssetPopup from "../../components/AssetsPopup";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import ConfirmPopup from "../../components/ConfirmPopup";
import DirectChat from "../../components/DirectChat";
import VideoPopup from "../../components/VideoPlayer";
import Loader from "../../components/Loader";

// Custom components
import TrendingSection from "./components/TrendingSection";
import ProfileSocialLinks from "./components/ProfileSocialLinks";
import ProfileStats from "./components/ProfileStats";

// Hooks
import useArtistProfile from "./hooks/useArtistProfileSimple";
import useArtistProfileActions from "./hooks/useArtistProfileActions";

// Utils
import { Images } from "../../assets/common";
import VerificationSVG from "../../assets/common/artistProfile/Verification.svg";
import { fullName, isOwnerId } from "../../constants/function";
import { addSongAction } from "../../action/songs";
import notifier from "../../components/Notifier";

import "./index.scss";

export default function ArtistProfile() {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  // Get URL params
  const params = new URLSearchParams(history.location.search);
  const referralId = params.get("referralId");

  // UI State
  const [video, setVideo] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState(t("success.message7"));
  const [openTip, setOpenTip] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewAsset, setViewAsset] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [menu, setMenu] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(0);

  // Use custom hooks - MUST be before any useEffect that uses these values
  const {
    loading,
    initialLoading,
    userData,
    trendingMusic,
    music,
    playlistMusic,
    nftMusic,
    albums,
    videos,
    getMyMusic,
    getPlayLists,
    getNfts,
    getAlbum,
    getVideos,
    getPlayListMusic,
    playlistNft,
    setUserData,
    setLoading,
    handleTabSwitch,
    handleScroll,
    active,
    loaded,
  } = useArtistProfile(id, referralId);
  
  // Debug loading states and user changes
  useEffect(() => {
    console.log('📊 Artist Profile Loading States:', {
      currentUrlId: id,
      loading,
      initialLoading,
      hasUserData: !!userData?._id,
      loadedUserId: userData?._id,
      userMismatch: id !== userData?._id && userData?._id
    });
    
    // Alert if we have a user mismatch (showing wrong user data)
    if (id && userData?._id && id !== userData._id) {
      console.error('⚠️ USER MISMATCH DETECTED!', {
        urlId: id,
        loadedUserId: userData._id,
        userName: (userData.firstName || '') + ' ' + (userData.lastName || '')
      });
    }
  }, [id, loading, initialLoading, userData]);

  // Validate ID and redirect if invalid
  useEffect(() => {
    if (!id || id === 'undefined' || id === 'null') {
      console.error('Invalid artist ID in URL:', id);
      notifier.error('Invalid artist profile');
      history.push('/app/Home');
    }
  }, [id, history]);
  
  // Redirect if user not found
  useEffect(() => {
    if (!loading && !initialLoading && id && !userData?._id && !userData?.error) {
      // Give it a moment to load, then redirect if still no user data
      const timer = setTimeout(() => {
        if (!userData?._id) {
          console.error('Artist not found, redirecting to home');
          history.push('/app/Home');
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [loading, initialLoading, userData, id, history]);

  const actions = useArtistProfileActions();

  // Handlers
  const handleTip = async (amount, id, type, currency) => {
    await actions.handleTip(amount, id, type, currency, setLoading, setOpenTip);
  };

  const subscribe = async () => {
    await actions.subscribe(id, userData, setUserData, setLoading, setOpen, setShowConfirm);
  };

  const unSubscribe = async () => {
    await actions.unSubscribe(id, userData, setUserData, setLoading, setMessage, setOpen);
  };

  const follow = async (artistId) => {
    await actions.follow(artistId, userData, setUserData, setLoading);
  };

  const handlePlayMusic = (music) => {
    if (!music["isSubscribed"] && music["private"]) {
      notifier.error(t("notifier.error.toast5"));
    } else {
      dispatch(addSongAction(music));
    }
  };

  const handlePlayPlaylist = (playlist) => {
    history.push(`/app/Music/${playlist?._id}`);
  };

  const handleAddToPlaylist = (musicId) => {
    setSelectedId(musicId);
    setShowPlaylist(true);
  };

  const handleClickOpen = () => {
    setViewAsset(true);
  };

  const handleClose = () => {
    setViewAsset(false);
  };

  const handleBottomNavChange = (event, newValue) => {
    setBottomNavValue(newValue);
    switch(newValue) {
      case 0:
        history.push('/app/Home');
        break;
      case 1:
        history.push('/app/Favorites');
        break;
      case 2:
        history.push('/app/MyMusic');
        break;
      case 3:
        history.push('/app/Collection');
        break;
      case 4:
        history.push('/app/Playlists');
        break;
      default:
        break;
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => {
      if (menu) setMenu(false);
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [menu]);

  // Reset chat when changing profiles
  useEffect(() => {
    setChatOpen(false);
  }, [id]);

  return (
    <Box sx={{ position: "relative", pb: 7 }}>
      <ProfileBanner
        userData={userData}
        edit={false}
        isArtist={true}
        loader={setLoading}
      />
      
      <Stack
        spacing={2}
        sx={{ textAlign: "center" }}
        className="_artistProfileBox"
      >
        {/* Profile Header */}
        <Stack alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              sx={{
                fontSize: "20px !important",
                marginLeft: userData["isVerified"]
                  ? "18px !important"
                  : "10px !important",
              }}
              variant="body1"
              color="default"
            >
              {fullName(userData)}
            </Typography>
            {userData["isVerified"] && (
              <img 
                src={VerificationSVG}
                alt="Verified"
                style={{ 
                  width: 18, 
                  height: 18,
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }} 
              />
            )}
          </Stack>

          {/* Social Links */}
          <ProfileSocialLinks
            twitter={userData?.twitterLink}
            instagram={userData?.instagramLink}
            tiktok={userData?.tikTokLink}
          />

          {/* Bio */}
          <Typography
            sx={{
              fontSize: "14px !important",
              color: "#ffffff70",
              padding: "10px 40px",
            }}
          >
            {userData?.bio ?? ""}
          </Typography>

          {/* Profile Stats */}
          <ProfileStats
            userData={userData}
            isOwnerId={isOwnerId(id)}
            onTipClick={() => setOpenTip(true)}
          />
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ gap: "24px", margin: "15px 0", position: "relative" }}
        >
          {!isOwnerId(id) && (
            <>
              {/* Follow Button */}
              <Button
                variant="contained"
                color={userData["isFollowed"] ? "success" : "primary"}
                sx={{
                  height: "36px !important",
                  width: "110px",
                  fontSize: "13px !important",
                }}
                onClick={() => follow(userData["_id"])}
                startIcon={
                  userData["isFollowed"] ? 
                    <PersonRemoveIcon sx={{ width: 16, height: 16 }} /> : 
                    <PersonAddIcon sx={{ width: 16, height: 16 }} />
                }
              >
                {userData["isFollowed"] === false
                  ? t("follow.followList.button1")
                  : t("follow.followList.button2")}
              </Button>

              {/* More Button with Dropdown */}
              <Box sx={{ position: "relative" }}>
                <Button
                  variant="outlined"
                  sx={{
                    height: "36px !important",
                    width: "110px",
                    fontSize: "13px !important",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenu(!menu);
                  }}
                  color="default"
                  endIcon={
                    <KeyboardArrowDownIcon
                      style={{
                        transform: menu ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  }
                >
                  {t("artistProfile.button3")}
                </Button>

                {/* Subscription Dropdown */}
                {menu && (
                  <Paper
                    elevation={3}
                    sx={{
                      background: "#121515",
                      borderRadius: "16px",
                      padding: "16px",
                      zIndex: 1,
                      position: "absolute",
                      top: "40px",
                      left: "0",
                      minWidth: "300px",
                      [theme.breakpoints.up("sm")]: {
                        left: "auto",
                        right: "0",
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row" justifyContent="space-between">
                      <Stack sx={{ textAlign: "left" }}>
                        <Typography
                          className="fs-13 fw-b"
                          style={{ color: "#5A5555" }}
                        >
                          {t("artistProfile.subscribe")}
                        </Typography>
                        <Typography
                          className="fs-20 light-text"
                          style={{ color: "#5A5555" }}
                        >
                          {userData["subscriptionFee"]} {t("artistProfile.coins")}{" "}
                          <small>{t("artistProfile.month")}</small>
                        </Typography>
                      </Stack>
                      
                      {userData["subscriptionStatus"] === "enabled" && (
                        <Box>
                          {userData["isSubscribed"] === true &&
                          userData["subscribedStatus"] !== "Unsubscribed" ? (
                            <Button
                              variant="outlined"
                              color="default"
                              sx={{
                                height: "36px !important",
                                width: "110px",
                                fontSize: "13px !important",
                              }}
                              onClick={() => unSubscribe()}
                            >
                              {t("artistProfile.button1")}
                            </Button>
                          ) : userData["isSubscribed"] === true &&
                            userData["subscribedStatus"] === "Unsubscribed" ? (
                            <Button
                              variant="outlined"
                              sx={{
                                height: "36px !important",
                                width: "auto",
                                fontSize: "13px !important",
                              }}
                              color="default"
                              disabled={true}
                            >
                              {t("artistProfile.button2")}
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              sx={{
                                height: "36px !important",
                                width: "110px",
                                fontSize: "13px !important",
                              }}
                              color="default"
                              onClick={() => setShowConfirm(true)}
                            >
                              {t("artistProfile.button3")}
                            </Button>
                          )}
                        </Box>
                      )}
                    </Stack>
                    
                    {userData["subscribedEndDate"] && (
                      <Typography color="primary" className="fs-12 text-right">
                        {t("artistProfile.text2")}{" "}
                        {moment(userData["subscribedEndDate"], "DD-MM-YYYY'").format(
                          "MM-DD-YYYY"
                        )}
                      </Typography>
                    )}

                    <Divider sx={{ my: 2, borderColor: "#4D4D4D" }} />
                    
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" gap="10px">
                        <LockIcon
                          sx={{
                            backgroundColor: "#BDBDBD",
                            borderRadius: "50%",
                            fontSize: "14px",
                            padding: "2px",
                          }}
                        />
                        <Typography
                          className="fs-14 text-left"
                          style={{ color: "#5A5555" }}
                        >
                          {t("artistProfile.text1")}
                        </Typography>
                      </Stack>

                      <Typography
                        onClick={handleClickOpen}
                        color="primary"
                        className="fs-14 nowrap _cp"
                        style={{ marginLeft: "5px" }}
                      >
                        {t("artistProfile.text2")}
                      </Typography>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </>
          )}
        </Stack>

        {/* Trending Section */}
        <TrendingSection
          trendingMusic={trendingMusic}
          onPlayMusic={handlePlayMusic}
          onPlayPlaylist={handlePlayPlaylist}
          onAddToPlaylist={handleAddToPlaylist}
          userName={fullName(userData)}
          id={id}
          onChatOpen={() => setChatOpen(true)}
        />

        <Divider sx={{ color: "#3E3E3E" }} orientation="horizontal" />

        {/* Artist Content Tabs */}
        <Stack>
          <ArtistContent
            id={id}
            userData={userData}
            getPlayListMusic={getPlayListMusic}
            playlistNft={playlistNft}
            initialMusic={music}
            initialPlaylists={playlistMusic}
            initialNfts={nftMusic}
            initialAlbums={albums}
            initialVideos={videos}
            getMyMusic={getMyMusic}
            getPlayLists={getPlayLists}
            getNfts={getNfts}
            getAlbum={getAlbum}
            getVideos={getVideos}
            handleTabSwitch={handleTabSwitch}
            handleScroll={handleScroll}
            active={active}
            loaded={loaded}
            loading={loading}
          />
        </Stack>
      </Stack>


      {/* Popups and Modals */}
      {chatOpen && (
        <DirectChat
          senderUserId={userData["_id"]}
          title={fullName(userData)}
          onOutsideClick={() => setChatOpen(false)}
          onOpenTip={() => setOpenTip(true)}
          artistChat={true}
        />
      )}
      
      {openTip && (
        <TipPopup
          artist={fullName(userData)}
          id={userData["_id"]}
          open={openTip}
          isArtist={true}
          handleClose={handleTip}
        />
      )}
      
      {open && (
        <SuccessPopup
          message={message}
          open={open}
          handleClose={() => setOpen(false)}
        />
      )}
      
      {viewAsset && (
        <AssetPopup
          id={userData["_id"]}
          open={viewAsset}
          handleClose={handleClose}
        />
      )}

      {showPlaylist && (
        <AddMusicToPlaylist
          musicId={selectedId}
          open={showPlaylist}
          handleClose={() => setShowPlaylist(false)}
        />
      )}
      
      {initialLoading && <Loader />}
      
      {showConfirm && (
        <ConfirmPopup
          open={showConfirm}
          id={userData["_id"]}
          index={0}
          message={`${t("confirmPopup.text3")} ${userData["subscriptionFee"]} SIKI coins.`}
          handleClose={(e) => (e ? subscribe() : setShowConfirm(false))}
        />
      )}
      
      {video && (
        <VideoPopup
          open={video ? true : false}
          video={video}
          handleClose={() => setVideo(null)}
        />
      )}
    </Box>
  );
}